import { lazy, Suspense, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Heart, Mail, Check, X, Loader2, Send, User as UserIcon, UserPlus, UserCheck, Sparkles, Coins, Receipt, Gift } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { fetchEngagement } from "../lib/publicEngagement";
import { toast } from "sonner";
import AuthTabs from "./AuthTabs";
import PhoneCountryInput from "./PhoneCountryInput";
// v149.64 — The in-store Redeem modal lives on the MyLoyalty page; loading
// it here at import time put that whole page in every bio visit's first
// download. It's fetched when a follower opens it.
const OfflineRedeemModal = lazy(() => import("../pages/MyLoyalty").then((m) => ({ default: m.OfflineRedeemModal })));

/**
 * <HubEngagementBar />
 *
 * Compact strip that sits under the creator's name on `/u/{slug}` public
 * bio hub. Shows Like heart + count, Follow (email capture) + count, and
 * a private "Send a private message" comment button. All three actions
 * hit `/api/public/creators/{slug}/…` endpoints.
 *
 * When the viewer is signed in as a Linkola user, Follow becomes a
 * 1-tap in-app action (POST /creators/{slug}/follow-me) that mirrors
 * into hub_followers so email broadcasts still reach them.
 *
 * Props: creatorSlug (required).
 */
export default function HubEngagementBar({ creatorSlug }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const { user } = useAuth();
  const [state, setState] = useState({ likes: 0, liked: false, followers: 0, visitor_id: null, loyalty_enabled: false, loyalty_follow_coins: 0, loyalty_coin_name: "coins" });
  const [followState, setFollowState] = useState({ known: false, following: false, is_self: false });
  const [followBusy, setFollowBusy] = useState(false);
  const [openFollow, setOpenFollow] = useState(false);
  const [openComment, setOpenComment] = useState(false);
  const [openThanks, setOpenThanks] = useState(null); // "follow" | "comment"
  // v143.17 — Signals from the last successful follow-modal submit that
  // FollowThanksModal reads: skip the "create a free account" nudge for
  // users who already have a Linkola account.
  const [thanksIsExistingUser, setThanksIsExistingUser] = useState(false);

  // v145.36 — Once the visitor is a signed-in follower on a loyalty
  // creator, swap the "Follow to earn" ribbon for a LIVE balance chip
  // + in-store Redeem button (same modal Feed uses). Refetched on
  // window `coins:changed` so a successful redemption instantly
  // updates the number.
  const [balance, setBalance] = useState(null);
  const [redeemOpen, setRedeemOpen] = useState(false);

  // Bootstrap counters + liked state
  useEffect(() => {
    if (!creatorSlug) return;
    (async () => {
      try {
        // v149.64 — Shared with useCreatorLoyalty: one request per page.
        const d = await fetchEngagement(creatorSlug);
        setState((s) => ({ ...s, ...d }));
        if (d.visitor_id) localStorage.setItem("hub_vid", d.visitor_id);
      } catch { /* silent */ }
    })();
  }, [backend, creatorSlug]);

  // If the viewer is a signed-in Linkola user, check whether they
  // already follow this creator so we can render "Follow" vs "Following".
  useEffect(() => {
    if (!creatorSlug || !user?.user_id) {
      setFollowState({ known: false, following: false, is_self: false });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/creators/${creatorSlug}/follow-me/state`);
        setFollowState({ known: true, following: !!data.following, is_self: !!data.is_self });
      } catch { setFollowState({ known: false, following: false, is_self: false }); }
    })();
  }, [creatorSlug, user?.user_id]);

  // v145.36 — Fetch this visitor's coin balance for the current creator
  // whenever they're signed in + loyalty is enabled. Refreshes on the
  // global `coins:changed` event so the chip is always in sync with
  // redemptions triggered from the Redeem modal or Feed page.
  //
  // v148.x-hotfix — Use the per-creator endpoint (returns 0 when no
  // account yet) instead of `/loyalty/my` (which only lists creators
  // where a balance was ever earned). This means a signed-in visitor
  // sees a "0 coins" chip immediately, so they know a program exists
  // and coins will accrue on their next action.
  useEffect(() => {
    if (!user?.user_id || !creatorSlug || !state.loyalty_enabled) {
      setBalance(null);
      return;
    }
    let cancelled = false;
    const refresh = async () => {
      try {
        const { data } = await api.get(
          `/public/creators/${encodeURIComponent(creatorSlug)}/loyalty/my-balance`,
        );
        if (cancelled) return;
        // Shape it like the old /loyalty/my entry so downstream
        // rendering (chip + Redeem modal) doesn't need to change.
        setBalance({
          business_slug: creatorSlug,
          business_user_id: data.business_user_id,
          business_name: data.business_name,
          balance: Number(data.balance || 0),
          lifetime_earned: Number(data.lifetime_earned || 0),
          coin_name: data.coin_name || "coins",
          value_per_coin_inr: Number(data.value_per_coin_inr || 0),
          min_redeem: Number(data.min_redeem || 1),
          min_purchase_inr: Number(data.min_purchase_inr || 0),
          max_discount_pct: Number(data.max_discount_pct || 0),
          discount_tiers: data.discount_tiers || [],
          gifts: data.gifts || [],
        });
      } catch { /* silent — leave chip hidden */ }
    };
    refresh();
    const handler = () => refresh();
    window.addEventListener("coins:changed", handler);
    return () => { cancelled = true; window.removeEventListener("coins:changed", handler); };
  }, [user?.user_id, creatorSlug, state.loyalty_enabled, followState.following]);

  const toggleLike = async () => {
    try {
      const r = await fetch(`${backend}/api/public/creators/${creatorSlug}/like`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visitor_id: state.visitor_id }),
      });
      const d = await r.json();
      setState((s) => ({ ...s, liked: d.liked, likes: d.total }));
    } catch { /* silent */ }
  };

  const bump = (delta) => setState((s) => ({ ...s, followers: s.followers + delta }));

  const isAuthed = !!user?.user_id;
  const canInappFollow = isAuthed && !followState.is_self;
  const canInappComment = isAuthed && !followState.is_self;

  const inappToggle = async () => {
    if (followBusy) return;
    setFollowBusy(true);
    try {
      if (followState.following) {
        const { data } = await api.delete(`/creators/${creatorSlug}/follow-me`);
        setFollowState((s) => ({ ...s, following: false }));
        setState((s) => ({ ...s, followers: data.followers ?? s.followers }));
        toast.success("Unfollowed");
      } else {
        const { data } = await api.post(`/creators/${creatorSlug}/follow-me`);
        setFollowState((s) => ({ ...s, following: true }));
        setState((s) => ({ ...s, followers: data.followers ?? s.followers }));
        if (!data.already) toast.success("Following — updates land in your Messages inbox");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update follow");
    } finally { setFollowBusy(false); }
  };

  // v145.35 — Show a small "Follow to earn coins & unlock discounts"
  // ribbon below the Like/Follow/Message chips when:
  //   - the creator has an active loyalty program with a follow reward
  //   - the visitor isn't already following (nudge is irrelevant then)
  //   - the visitor isn't the creator themselves
  //
  // v145.36 — Once the visitor IS following AND signed in, swap for a
  // live-balance chip + in-store Redeem CTA so returning fans see
  // what they can spend without hopping to /app/my-loyalty.
  //
  // v148.x-hotfix — Extended: any signed-in visitor on a loyalty-
  // enabled bio sees the chip, even before they follow. It renders
  // their current balance (0 for brand-new visitors) so they know a
  // program exists and coins will accrue on their next action. The
  // legacy "Follow to earn N coins" ribbon still shows for
  // *anonymous* visitors — swapping it for the balance chip the
  // moment they sign in.
  const isSignedIn = Boolean(user?.user_id) && !followState.is_self;
  const showBalanceChip = Boolean(
    state.loyalty_enabled && isSignedIn && balance,
  );
  // Anonymous visitors still get the follow-to-earn ribbon.
  const showLoyaltyEarn = Boolean(
    state.loyalty_enabled &&
    state.loyalty_follow_coins > 0 &&
    !isSignedIn &&
    !followState.following &&
    !followState.is_self,
  );
  const coinBal = balance ? Number(balance.balance || 0) : 0;
  const canRedeem = balance && coinBal >= Number(balance.min_redeem || 0) && coinBal > 0;

  return (
    <div className="flex flex-col items-center gap-2 mt-2 w-full max-w-md mx-auto">
    <div
      className="flex items-stretch justify-center gap-2 w-full"
      data-testid="hub-engagement-bar"
    >
      <ChipButton
        onClick={toggleLike}
        icon={<Heart className={`w-4 h-4 ${state.liked ? "fill-current" : ""}`} strokeWidth={2.5} />}
        label="Like"
        count={state.likes}
        active={state.liked}
        activeBg="bg-coral text-white border-coral"
        idleBg="bg-white hover:bg-cream"
        testid="hub-like-btn"
        countTestId="hub-like-count"
        ariaPressed={state.liked}
      />

      {followState.is_self ? (
        // v96 — Always show Follow chip so followers count is visible even
        // when the creator is viewing their own bio. Disabled + labelled
        // "You" so they know they can't follow themselves.
        <ChipButton
          icon={<UserCheck className="w-4 h-4" strokeWidth={2.5} />}
          label="You"
          count={state.followers}
          disabled
          idleBg="bg-white opacity-60 cursor-not-allowed"
          testid="hub-follow-btn-self"
          countTestId="hub-follow-count"
        />
      ) : canInappFollow ? (
        <ChipButton
          onClick={inappToggle}
          disabled={followBusy}
          icon={followBusy
            ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} />
            : followState.following
              ? <UserCheck className="w-4 h-4" strokeWidth={2.5} />
              : <UserPlus className="w-4 h-4" strokeWidth={2.5} />}
          label={followState.following ? "Following" : "Follow"}
          count={state.followers}
          active={followState.following}
          activeBg="bg-mint text-ink border-ink"
          idleBg="bg-white hover:bg-mint"
          testid={followState.following ? "hub-follow-btn-following" : "hub-follow-btn-inapp"}
          countTestId="hub-follow-count"
          ariaPressed={followState.following}
        />
      ) : (
        <ChipButton
          onClick={() => setOpenFollow(true)}
          icon={<Mail className="w-4 h-4" strokeWidth={2.5} />}
          label="Follow"
          count={state.followers}
          idleBg="bg-white hover:bg-mint"
          testid="hub-follow-btn"
          countTestId="hub-follow-count"
        />
      )}

      <ReferChipButton creatorSlug={creatorSlug} />

      {openFollow && !canInappFollow && (
        <FollowAuthModal
          creatorSlug={creatorSlug}
          loyaltyFollowCoins={state.loyalty_follow_coins}
          loyaltyCoinName={state.loyalty_coin_name}
          requireFollowOtp={!!state.require_follow_otp}
          onClose={() => setOpenFollow(false)}
          onFollowed={(res) => {
            if (typeof res.followers === "number") {
              setState((s) => ({ ...s, followers: res.followers }));
            } else if (!res.already) {
              bump(1);
            }
            // v146 — Anonymous follows are still "known" from the
            // creator's side (backend has their email/phone), but we
            // don't flip the local `following` chip because there's no
            // session token to bind them to a Linkola account. Any
            // upsell-then-signup path DOES set the chip via
            // handleAccountCreated → /follow-me.
            if (Number(res.loyalty_awarded) > 0) {
              setFollowState({ known: true, following: true, is_self: false });
              const coinLabel = res.coin_name || state.loyalty_coin_name;
              // v146.64 — Phone-first follows now earn coins. Toast
              // includes a "See balance" action when a passbook token
              // came back (walk-in follow path).
              if (res.passbook_token) {
                toast.success(
                  `+${res.loyalty_awarded} ${coinLabel} earned!`,
                  {
                    action: {
                      label: "See balance",
                      onClick: () => window.open(`/passbook/${res.passbook_token}`, "_blank"),
                    },
                    duration: 8000,
                  },
                );
              } else {
                toast.success(`+${res.loyalty_awarded} ${coinLabel} earned!`);
              }
            } else if (res.followers && res.followers !== state.followers) {
              // Real follow through the no-login path.
              toast.success("Following — you'll get updates via email / WhatsApp");
            }
            setOpenFollow(false);
          }} />
      )}
      {openComment && (
        canInappComment ? (
          <CommentModal creatorSlug={creatorSlug} visitorId={state.visitor_id}
            authedUser={user}
            onClose={() => setOpenComment(false)}
            onSuccess={() => { setOpenThanks("comment"); setOpenComment(false); }} />
        ) : (
          <MessageAuthModal creatorSlug={creatorSlug} visitorId={state.visitor_id}
            onClose={() => setOpenComment(false)}
            onSent={() => { setOpenThanks("comment"); setOpenComment(false); }} />
        )
      )}
      {openThanks === "follow" ? (
        <FollowThanksModal
          isExistingUser={thanksIsExistingUser}
          onClose={() => { setOpenThanks(null); setThanksIsExistingUser(false); }}
        />
      ) : openThanks === "comment" ? (
        <ThanksToast kind={openThanks} onClose={() => setOpenThanks(null)} />
      ) : null}
    </div>
    {showBalanceChip ? (
      <div
        className="w-full flex items-center justify-between gap-2 border-2 border-ink bg-cream px-3 py-1.5 shadow-brutalSm"
        data-testid="hub-loyalty-balance-chip"
      >
        <span className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-black text-ink min-w-0">
          <Coins className="w-3.5 h-3.5 text-coral shrink-0" strokeWidth={3} />
          <span className="tabular-nums">{coinBal}</span>
          <span className="text-ink/70 font-bold truncate">
            {balance.coin_name || "coins"} ready to spend
          </span>
        </span>
        <button
          type="button"
          onClick={() => canRedeem && setRedeemOpen(true)}
          disabled={!canRedeem}
          data-testid="hub-loyalty-redeem-btn"
          title={canRedeem ? "Redeem coins in-store" : `Need at least ${balance.min_redeem || 1} to redeem`}
          className="inline-flex items-center gap-1 border-2 border-ink bg-coral text-white px-2.5 py-1 text-[11px] font-black shadow-brutalSm shrink-0 disabled:opacity-40 disabled:cursor-not-allowed active:translate-y-[1px]"
        >
          <Receipt className="w-3 h-3" strokeWidth={3} />
          Redeem
        </button>
      </div>
    ) : showLoyaltyEarn ? (
      <button
        type="button"
        onClick={() => (canInappFollow ? inappToggle() : setOpenFollow(true))}
        disabled={followBusy}
        data-testid="hub-loyalty-follow-ribbon"
        className="w-full inline-flex items-center justify-center gap-2 border-2 border-ink bg-sun/70 hover:bg-sun px-3 py-1.5 text-[11px] sm:text-xs font-bold text-ink shadow-brutalSm transition-all active:translate-y-[1px] disabled:opacity-60"
      >
        <Coins className="w-3.5 h-3.5 shrink-0" strokeWidth={2.5} />
        <span className="truncate">
          Follow to earn{" "}
          <span className="font-black tabular-nums">
            {state.loyalty_follow_coins} {state.loyalty_coin_name}
          </span>
          {" "}&amp; unlock discounts
        </span>
      </button>
    ) : null}
    {redeemOpen && balance && (
      <Suspense fallback={null}>
        <OfflineRedeemModal
          balance={balance}
          onClose={() => setRedeemOpen(false)}
          onDone={() => setRedeemOpen(false)}
        />
      </Suspense>
    )}
    </div>
  );
}


/**
 * Small compact engagement chip. Deliberately does NOT use the .ah-block
 * class (which forces `width: 100%` and huge padding — that would stack
 * the three chips into big vertical bars). Uses `--ah-fg` for border so
 * it still respects the creator's Bio Hub theme.
 */
function ChipButton({ onClick, icon, label, count, active, activeBg, idleBg,
                     testid, countTestId, ariaPressed, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={ariaPressed}
      data-testid={testid}
      className={`flex-1 basis-0 min-w-0 inline-flex flex-col items-center justify-center gap-0.5 px-2 py-2 border-2 text-[11px] font-black uppercase tracking-wider transition-all active:translate-y-[1px] disabled:opacity-60 ${active ? activeBg : idleBg}`}
      style={{ borderColor: "var(--ah-fg, currentColor)" }}
    >
      <div className="flex items-center gap-1">
        {icon}
        {typeof count === "number" && (
          <span
            data-testid={countTestId}
            className={`text-[10px] font-black leading-none px-1 py-[1px] border ${active ? "bg-white/20 border-white/30 text-white" : "bg-cream border-ink/20 text-ink/80"}`}
          >
            {count}
          </span>
        )}
      </div>
      <span className="leading-none">{label}</span>
    </button>
  );
}


/**
 * ReferChipButton — v147.99.29 (Feb 2026)
 *
 * Replaces the old "Message" chip. Fetches the caller's personal share
 * URL via `/public/creators/:slug/my-referral-account` (auto-provisions
 * an empty loyalty account when needed) and, on click, opens the native
 * share sheet (mobile) or copies to clipboard (desktop / no Web Share).
 *
 * When the visitor isn't signed in or the creator's referral program is
 * off, we fall back to a plain share of the creator's bio URL — the
 * button never disappears, so top-nav layout stays stable.
 */
function ReferChipButton({ creatorSlug }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const openShare = async () => {
    if (busy) return;
    setBusy(true);
    let shareUrl = `${window.location.origin}/u/${encodeURIComponent(creatorSlug)}`;
    let shareText = `Check out @${creatorSlug} on Linkola`;
    try {
      const r = await fetch(
        `${backend}/api/public/creators/${encodeURIComponent(creatorSlug)}/my-referral-account`,
        { credentials: "include" },
      );
      if (r.ok) {
        const d = await r.json();
        if (d?.eligible && d?.share_url) {
          shareUrl = d.share_url;
          const coin = d.coin_name || "coins";
          const amt = d.earn_amount || 0;
          shareText = amt
            ? `Join me on @${creatorSlug} — you get updates, I earn ${amt} ${coin}.`
            : `Check out @${creatorSlug} on Linkola`;
        }
      }
    } catch { /* fall through to plain bio share */ }

    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title: shareText, text: shareText, url: shareUrl });
      } else if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        toast.success("Share link copied");
        setTimeout(() => setCopied(false), 1800);
      } else {
        toast.info(shareUrl);
      }
    } catch (e) {
      // AbortError (user closed the sheet) is not a real failure — stay quiet.
      if (e?.name === "AbortError") return;
      // v148.x-hotfix — Some in-app browsers (Capacitor WKWebView,
      // Instagram, Facebook) throw when navigator.share is called
      // even though the API exists. Fall back to clipboard so the
      // customer can still paste the referral link into WhatsApp /
      // SMS / anywhere else. Only escalate to a toast error if BOTH
      // paths fail.
      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(shareUrl);
          setCopied(true);
          toast.success("Link copied — paste into WhatsApp or SMS");
          setTimeout(() => setCopied(false), 1800);
        } else {
          toast.info(shareUrl);
        }
      } catch {
        toast.error("Couldn't open share sheet");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <ChipButton
      onClick={openShare}
      icon={
        busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} />
             : copied ? <Check className="w-4 h-4" strokeWidth={2.5} />
             : <Gift className="w-4 h-4" strokeWidth={2.5} />
      }
      label={copied ? "Copied" : "Refer"}
      idleBg="bg-white hover:bg-sun"
      testid="hub-refer-btn"
      disabled={busy}
    />
  );
}


/**
 * FollowContactModal — v146 (Feb 2026)
 *
 * Anonymous visitors follow with ONLY an email or a phone — no login,
 * no signup needed. Broadcasts still reach them because
 * `hub_engagement_service.follow()` now indexes on either channel.
 *
 * Copy is deliberately explicit so followers know exactly what they're
 * getting into and what upgrading to a full account buys them:
 *   • Just follow (email or phone) → get message updates + offers via
 *     that channel. Nothing else asked.
 *   • Sign up (offered as an optional secondary CTA at the bottom) →
 *     unlocks the in-app inbox AND coin redemption. Only required for
 *     the loyalty program.
 */
function FollowContactModal({ creatorSlug, loyaltyFollowCoins, loyaltyCoinName,
                              requireFollowOtp = false,
                              onClose, onFollowed }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const hasCoins = Number(loyaltyFollowCoins) > 0;
  // Radio-style toggle between phone and email as the primary field —
  // keeps the modal short so mobile viewers see the submit button
  // above the fold. v146.56 — Phone is the default because that's the
  // primary channel for WhatsApp / SMS updates in India.
  const [channel, setChannel] = useState("phone");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  // v146.64 — WhatsApp opt-in for phone-first follows. Defaults ON so
  // the brand can broadcast to opted-in followers by default (users
  // can still uncheck before submitting). Ignored for email-first
  // follows.
  const [waOptIn, setWaOptIn] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showSignup, setShowSignup] = useState(false);
  // v148.x — Follow OTP flow state. When the creator has
  // `require_follow_otp` set AND the brand wallet has funds, we ask
  // for a 6-digit SMS code before submitting the follow. Empty wallet
  // returns skip_otp=true and we fall through to the direct submit.
  const [otpStep, setOtpStep] = useState(false); // "verify" step
  const [otpChallenge, setOtpChallenge] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpChannel, setOtpChannel] = useState("");

  const _splitPhone = (raw) => {
    // Value from PhoneCountryInput is "+91XXXXXXXXXX". Split into
    // country_code + local digits so the OTP-start endpoint gets
    // properly-shaped inputs.
    const clean = String(raw || "").trim();
    const m = clean.match(/^\+(\d{1,4})(\d+)$/);
    if (!m) return { cc: "91", digits: clean.replace(/\D/g, "") };
    return { cc: m[1], digits: m[2] };
  };

  const submitDirect = async (bodyExtras = {}) => {
    const body = { name: name || null, ...bodyExtras };
    if (channel === "email") {
      body.email = email.trim();
    } else {
      body.phone = phone.trim();
      body.wa_opt_in = !!waOptIn;
    }
    const r = await fetch(`${backend}/api/public/creators/${creatorSlug}/follow`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(d.detail || "follow_failed");
    }
    const data = await r.json().catch(() => ({}));
    onFollowed({
      already: !!data.already,
      isExistingUser: !!data.is_existing_user,
      followers: typeof data.followers === "number" ? data.followers : null,
      // v146.64 — Backend now awards follow-coins for phone follows too.
      loyalty_awarded: Number(data.loyalty_awarded || 0),
      coin_name: data.coin_name || loyaltyCoinName,
      passbook_token: data.passbook_token || null,
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (channel === "email") {
        if (!email.trim()) { setErr("Enter your email."); setBusy(false); return; }
        await submitDirect();
        return;
      }
      // Phone channel.
      if (!phone.trim() || phone.replace(/\D/g, "").length < 8) {
        setErr("Enter a valid phone number.");
        setBusy(false); return;
      }
      // v148.x — If the creator requires OTP verification, kick off the
      // start flow. Wallet-empty → skip_otp true → submit direct.
      // Wallet-funded → present the OTP step.
      if (requireFollowOtp) {
        const { cc, digits } = _splitPhone(phone);
        const startResp = await fetch(
          `${backend}/api/public/creators/${creatorSlug}/follow/otp/start`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ country_code: cc, phone: digits }),
          },
        );
        if (!startResp.ok) {
          const d = await startResp.json().catch(() => ({}));
          throw new Error(d.detail || "otp_start_failed");
        }
        const startData = await startResp.json().catch(() => ({}));
        if (startData.skip_otp) {
          // Brand wallet empty — proceed unverified.
          await submitDirect();
          return;
        }
        setOtpChallenge(startData.challenge_id || "");
        setOtpChannel(startData.channel || "sms");
        setOtpStep(true);
        setBusy(false);
        return;
      }
      // No OTP required — submit follow directly.
      await submitDirect();
    } catch (ex) {
      setErr(ex.message === "follow_failed"
        ? "Please double-check your email/phone."
        : String(ex.message).replace(/^Error:\s*/, ""));
    } finally {
      setBusy(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{4,8}$/.test(otpCode)) {
      setErr("Enter the 6-digit code we sent you.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await submitDirect({
        otp_challenge_id: otpChallenge,
        otp_code: otpCode,
      });
    } catch (ex) {
      setErr(String(ex.message).replace(/^Error:\s*/, ""));
    } finally { setBusy(false); }
  };

  // Second-stage: upgrade to an account. Reuses the existing AuthTabs
  // component. On success, the outer component's follow-me path takes
  // over so coins get awarded automatically.
  const handleAccountCreated = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/creators/${creatorSlug}/follow-me`);
      onFollowed({
        already: !!data.already,
        followers: typeof data.followers === "number" ? data.followers : null,
        loyalty_awarded: Number(data.loyalty_awarded || 0),
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Account made — but couldn't finish following. Tap Follow again.");
      onClose();
    } finally { setBusy(false); }
  };

  return (
    createPortal(
    <div
      className="fixed inset-0 bg-black/60 z-[9997] flex items-end sm:items-center justify-center p-0 sm:p-3"
      onClick={onClose}
      data-testid="hub-follow-contact-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-page border-2 border-ink shadow-brutal w-full sm:max-w-md overflow-y-auto overscroll-contain flex flex-col"
        style={{
          maxHeight: "min(100dvh, 100vh)",
          // v148.x — Extra 24px buffer so the last CTA / signup nudge
          // isn't glued to the phone's home-indicator / gesture bar and
          // the modal can be scrolled fully without clipping.
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
        }}
        data-testid="hub-follow-contact-modal"
      >
        {/* Header */}
        <div className="border-b-2 border-ink px-4 py-4 flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-black text-xl leading-tight">
              {showSignup ? "Create your account" : "Follow for updates"}
            </h3>
            <p className="text-[13px] text-ink/70 mt-1 leading-snug">
              {showSignup
                ? `Sign up to open the in-app inbox${hasCoins ? ` and redeem ${loyaltyCoinName}` : ""}. Uses the same email or phone.`
                : "No account needed — we'll send updates & offers to your email or WhatsApp/SMS."}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="hub-follow-contact-close"
            className="shrink-0 border-2 border-ink bg-white p-1.5 shadow-brutalSm hover:bg-coral hover:text-white"
          >
            <X className="w-4 h-4" strokeWidth={3} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 pb-6">
          {showSignup ? (
            busy ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm font-bold text-ink/70">
                <Loader2 className="w-4 h-4 animate-spin" /> Wrapping up…
              </div>
            ) : (
              <>
                <AuthTabs
                  defaultTab="phone"
                  initialMode="signup"
                  compact
                  onSuccess={handleAccountCreated}
                />
                <button
                  type="button"
                  onClick={() => setShowSignup(false)}
                  data-testid="hub-follow-signup-back"
                  className="mt-3 w-full text-[11px] font-bold text-ink/60 underline"
                >
                  ← Back to just follow (no account)
                </button>
              </>
            )
          ) : otpStep ? (
            <form onSubmit={submitOtp} className="space-y-4" data-testid="hub-follow-otp-form">
              <div className="border-2 border-ink bg-mint/30 px-3 py-2 text-[13px] leading-snug">
                We sent a 6-digit code via {otpChannel === "whatsapp" ? "WhatsApp" : "SMS"} to{" "}
                <b>{phone}</b>. Enter it below to complete your follow.
              </div>
              <input
                autoFocus
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                data-testid="hub-follow-otp-input"
                className="w-full border-2 border-ink px-3 py-3 text-2xl font-mono tracking-[0.5em] text-center bg-white"
              />
              {err && (
                <p className="text-sm text-coral font-bold" data-testid="hub-follow-otp-err">{err}</p>
              )}
              <button
                type="submit"
                disabled={busy || otpCode.length < 4}
                data-testid="hub-follow-otp-submit"
                className="w-full border-2 border-ink bg-ink text-white font-bold text-base py-3 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Verify & follow
              </button>
              <button
                type="button"
                onClick={() => { setOtpStep(false); setOtpCode(""); setErr(""); }}
                data-testid="hub-follow-otp-back"
                className="w-full text-[12px] font-bold text-ink/60 underline"
              >
                ← Use a different number
              </button>
            </form>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="hub-follow-contact-form">
              {/* Channel toggle */}
              <div className="flex border-2 border-ink" role="tablist">
                {[
                  { key: "phone", label: "Phone / WhatsApp" },
                  { key: "email", label: "Email" },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={channel === tab.key}
                    onClick={() => { setChannel(tab.key); setErr(""); }}
                    data-testid={`hub-follow-tab-${tab.key}`}
                    className={`flex-1 py-2.5 text-sm font-black uppercase tracking-wider ${
                      channel === tab.key
                        ? "bg-ink text-white"
                        : "bg-white text-ink hover:bg-cream"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Contact input */}
              {channel === "email" ? (
                <input
                  key="email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  placeholder="you@email.com"
                  data-testid="hub-follow-email"
                  className="w-full border-2 border-ink px-3 py-3 text-base bg-white"
                />
              ) : (
                <>
                  {/* v148.x — `size="lg"` keeps country-code + 10-digit
                      on one line and enables native tel-autofill so the
                      customer's own number surfaces in the keyboard bar. */}
                  <PhoneCountryInput
                    value={phone}
                    onChange={setPhone}
                    required
                    size="lg"
                    testidPrefix="hub-follow-phone"
                    placeholder="98765 43210"
                  />
                  {/* v146.64 — WhatsApp opt-in. Default on; user can uncheck. */}
                  <label
                    className="flex items-start gap-2 border-2 border-ink/20 bg-mint/20 px-3 py-2.5 cursor-pointer select-none"
                    data-testid="hub-follow-wa-optin-row"
                  >
                    <input
                      type="checkbox"
                      checked={waOptIn}
                      onChange={(e) => setWaOptIn(e.target.checked)}
                      data-testid="hub-follow-wa-optin"
                      className="mt-0.5 shrink-0 w-4 h-4 accent-ink"
                    />
                    <span className="text-[13px] leading-snug">
                      <b>Send me updates & offers on WhatsApp</b>
                      <span className="block text-ink/60 mt-0.5 text-[12px]">
                        Uncheck to only receive updates when you open the page.
                      </span>
                    </span>
                  </label>
                </>
              )}

              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name (optional)"
                autoComplete="name"
                data-testid="hub-follow-name"
                className="w-full border-2 border-ink px-3 py-3 text-base bg-white"
              />

              {err && (
                <p className="text-sm text-coral font-bold" data-testid="hub-follow-err">{err}</p>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid="hub-follow-submit"
                className="w-full border-2 border-ink bg-ink text-white font-bold text-base py-3 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Follow this creator
              </button>

              {/* Optional signup nudge */}
              <div className="pt-3 mt-2 border-t border-ink/10">
                <div className="flex items-start gap-2">
                  {hasCoins ? (
                    <Coins className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={3} />
                  ) : (
                    <Sparkles className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={3} />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-bold leading-snug">
                      {hasCoins
                        ? `Want to earn ${loyaltyFollowCoins} ${loyaltyCoinName} & redeem discounts?`
                        : "Want messages inside the app instead?"}
                    </p>
                    <p className="text-[12px] text-ink/60 leading-snug mt-0.5">
                      {hasCoins
                        ? `Sign up (free) to unlock the in-app inbox + ${loyaltyCoinName} redemption. Optional — updates still reach you above.`
                        : "Sign up (free) to see this creator's updates in your Linkola inbox. Totally optional."}
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowSignup(true)}
                      data-testid="hub-follow-upsell"
                      className="mt-2 text-[13px] font-black underline text-ink"
                    >
                      Create a free account →
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body,
    )
  );
}

// v146 — Backward-compat alias. Other call sites still import
// `FollowAuthModal`; keeping the export means we don't have to churn
// them all in one PR.
const FollowAuthModal = FollowContactModal;


function FollowModal({ creatorSlug, onClose, onSuccess }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      const r = await fetch(`${backend}/api/public/creators/${creatorSlug}/follow`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name: name || null }),
      });
      if (!r.ok) throw new Error("bad_email");
      // v143.17 — Parse the response body and forward the useful flags so
      // the parent can (a) SET (not increment) the count and (b) skip the
      // sign-up nudge for people who already have a Linkola account.
      const data = await r.json().catch(() => ({}));
      onSuccess({
        already: !!data.already,
        isExistingUser: !!data.is_existing_user,
        followers: typeof data.followers === "number" ? data.followers : null,
      });
    } catch { setErr("Please enter a valid email."); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell onClose={onClose} testid="hub-follow-modal" title="Follow me for updates">
      <p className="text-xs opacity-70 mb-3">
        Get an email whenever this page is updated with something new. No spam — unsubscribe anytime with one click.
      </p>
      <form onSubmit={submit} className="space-y-2">
        <input
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          className="w-full border-2 border-ink px-2 py-1.5 text-sm bg-white"
          data-testid="hub-follow-name" />
        <input
          value={email} onChange={(e) => setEmail(e.target.value)}
          type="email" required autoFocus
          placeholder="Your email"
          className="w-full border-2 border-ink px-2 py-1.5 text-sm bg-white"
          data-testid="hub-follow-email" />
        {err && <p className="text-xs text-coral" data-testid="hub-follow-err">{err}</p>}
        <button
          type="submit" disabled={saving}
          className="w-full border-2 border-ink bg-ink text-white font-bold text-sm py-2 flex items-center justify-center gap-2"
          data-testid="hub-follow-submit">
          {saving && <Loader2 className="w-3 h-3 animate-spin" />} Follow
        </button>
      </form>
    </ModalShell>
  );
}


function CommentModal({ creatorSlug, visitorId, authedUser, onClose, onSuccess }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const [state, setState] = useState({
    message: "",
    name: authedUser?.name || "",
    email: authedUser?.email || "",
  });
  const [saving, setSaving] = useState(false);
  const isAuthed = !!authedUser?.user_id;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        message: state.message,
        name: state.name?.trim() || null,
        email: state.email?.trim() || null,
        visitor_id: visitorId,
      };
      const r = await fetch(`${backend}/api/public/creators/${creatorSlug}/comment`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error("send_failed");
      onSuccess();
    } catch (err) {
      console.error("hub comment submit failed", err);
      // Surface a soft error inline instead of a fake success toast.
      alert("Message could not be sent. Please try again.");
    }
    finally { setSaving(false); }
  };

  return (
    <ModalShell onClose={onClose} testid="hub-comment-modal" title="Send a private message">
      <p className="text-xs opacity-70 mb-3">
        Only the creator can read this. Perfect for feedback, collab requests or a quick hello.
        {isAuthed && (
          <>
            {" "}Sending as <b>{authedUser.name || authedUser.email || "you"}</b>.
          </>
        )}
      </p>
      <form onSubmit={submit} className="space-y-2">
        <textarea
          value={state.message}
          onChange={(e) => setState({ ...state, message: e.target.value })}
          required rows={4} maxLength={2000} placeholder="Your message…"
          autoFocus
          className="w-full border-2 border-ink px-2 py-1.5 text-sm bg-white"
          data-testid="hub-comment-message" />
        {!isAuthed && (
          <div className="grid grid-cols-2 gap-2">
            <input value={state.name}
              onChange={(e) => setState({ ...state, name: e.target.value })}
              placeholder="Your name (optional)"
              className="border-2 border-ink px-2 py-1.5 text-sm bg-white"
              data-testid="hub-comment-name" />
            <input value={state.email}
              onChange={(e) => setState({ ...state, email: e.target.value })}
              type="email" placeholder="Email (optional)"
              className="border-2 border-ink px-2 py-1.5 text-sm bg-white"
              data-testid="hub-comment-email" />
          </div>
        )}
        <button
          type="submit" disabled={saving}
          className="w-full border-2 border-ink bg-ink text-white font-bold text-sm py-2 flex items-center justify-center gap-2"
          data-testid="hub-comment-submit">
          {saving && <Loader2 className="w-3 h-3 animate-spin" />}
          <Send className="w-3 h-3" /> Send
        </button>
      </form>
    </ModalShell>
  );
}


/**
 * MessageAuthModal — v144.2. Two-step modal for anonymous visitors:
 *   Step 1 (auth): sign in via Phone or Email — matches the Follow modal
 *                  pattern so both engagement CTAs behave identically.
 *   Step 2 (compose): message textarea (auto-filled name/email from the
 *                  newly-created session). Same POST /public/…/comment
 *                  endpoint downstream.
 *
 * No coins ribbon on this modal — messages don't award loyalty coins today.
 */
function MessageAuthModal({ creatorSlug, visitorId, onClose, onSent }) {
  const [step, setStep] = useState("auth"); // "auth" | "compose"
  const [authedUser, setAuthedUser] = useState(null);

  const handleAuthSuccess = (data) => {
    // Session cookie is set by the auth call; capture the user so the
    // compose step can pre-fill + skip the anon fields.
    setAuthedUser(data?.user || null);
    setStep("compose");
  };

  if (step === "compose" && authedUser) {
    return (
      <CommentModal
        creatorSlug={creatorSlug}
        visitorId={visitorId}
        authedUser={authedUser}
        onClose={onClose}
        onSuccess={onSent}
      />
    );
  }

  return (
    createPortal(
    <div
      className="fixed inset-0 bg-black/60 z-[9997] flex items-end sm:items-center justify-center p-0 sm:p-3 overflow-y-auto"
      onClick={onClose}
      data-testid="hub-message-auth-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-page border-2 border-ink shadow-brutal w-full sm:max-w-md max-h-[100dvh] overflow-y-auto"
        data-testid="hub-message-auth-modal"
      >
        <div className="border-b-2 border-ink px-4 py-3 flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-black text-lg leading-tight">
              Sign in to send a private message
            </h3>
            <p className="text-[11px] text-ink/70 mt-0.5 leading-snug">
              Verified messages only — no spam, no bots. Only the creator can read it.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="hub-message-auth-close"
            className="shrink-0 border-2 border-ink bg-white p-1 shadow-brutalSm hover:bg-coral hover:text-white"
          >
            <X className="w-3.5 h-3.5" strokeWidth={3} />
          </button>
        </div>
        <div className="p-4">
          <AuthTabs
            defaultTab="phone"
            initialMode="login"
            compact
            onSuccess={handleAuthSuccess}
          />
        </div>
      </div>
    </div>,
    document.body,
    )
  );
}


function ModalShell({ children, onClose, testid, title }) {
  return (
    createPortal(
    <div className="fixed inset-0 bg-black/50 z-[9997] flex items-center justify-center p-3"
         onClick={onClose} data-testid={`${testid}-backdrop`}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border-2 border-ink shadow-brutal w-full max-w-md p-4"
        data-testid={testid}
      >
        <div className="flex items-center gap-2 mb-3">
          <h3 className="font-display font-black text-lg flex-1">{title}</h3>
          <button onClick={onClose} className="border border-ink p-1"
            data-testid={`${testid}-close`}><X className="w-3 h-3" /></button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
    )
  );
}


function ThanksToast({ kind, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  const msg = kind === "follow"
    ? "You're subscribed — we'll email you when this page updates."
    : "Message delivered. The creator will see it in their inbox.";
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-ink text-white px-4 py-2 border-2 border-ink shadow-brutal inline-flex items-center gap-2 text-sm font-bold"
      data-testid={`hub-engagement-toast-${kind}`}>
      <Check className="w-3.5 h-3.5" /> {msg}
    </div>
  );
}


/**
 * Success dialog shown to anonymous visitors right after they follow via
 * email capture.
 *
 * Two variants, distinguished by `isExistingUser`:
 *   * **new visitor** (default) — original conversion nudge with a
 *     Create-free-account CTA + benefit bullets.
 *   * **existing Linkola user** — the email they typed matches an
 *     existing account. Showing the Sign-Up nudge here would be both
 *     wrong ("you already have an account") and pushy, so we swap in a
 *     Sign-In CTA + a note explaining that future updates will land
 *     in-app once they sign in. Same modal shell so visual continuity is
 *     preserved.
 *
 * Signed-in users never see either variant — they take the 1-tap in-app
 * follow path and skip the modal entirely.
 */
function FollowThanksModal({ onClose, isExistingUser = false }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    createPortal(
    <div className="fixed inset-0 z-[9998] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4"
         data-testid="hub-follow-thanks-modal"
         data-existing-user={isExistingUser ? "1" : "0"}>
      <div className="w-full sm:max-w-md bg-page border-2 border-ink shadow-brutal">
        <div className="bg-mint border-b-2 border-ink px-4 py-3 flex items-center gap-2">
          <Check className="w-5 h-5" strokeWidth={3} />
          <p className="font-display font-black text-lg">You&apos;re in.</p>
          <button onClick={onClose} aria-label="Close"
                  data-testid="hub-follow-thanks-close"
                  className="ml-auto border-2 border-ink bg-white p-1 shadow-brutalSm hover:bg-coral hover:text-white">
            <X className="w-3.5 h-3.5" strokeWidth={3} />
          </button>
        </div>
        {isExistingUser ? (
          // v143.17 — Existing-user variant. No sign-up nudge, no
          // benefits list — this person is already a Linkola user, they
          // just aren't signed in on this device.
          <div className="p-4 space-y-4" data-testid="hub-follow-thanks-existing">
            <p className="text-sm text-ink/80">
              We&apos;ll email you when this creator updates their page.
            </p>
            <div className="border-2 border-ink bg-sun p-3 shadow-brutalSm space-y-2">
              <p className="font-display font-black text-base">Welcome back 👋</p>
              <p className="text-[13px] text-ink/80 leading-relaxed">
                Looks like you already have a <b>Linkola account</b> with this
                email. Sign in and this follow will move into your Feed —
                instant notifications, no email delay.
              </p>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Link
                  to="/#signin"
                  onClick={onClose}
                  data-testid="hub-follow-thanks-signin"
                  className="border-2 border-ink bg-coral text-white px-3 py-2 font-bold text-xs shadow-brutalSm hover:bg-ink hover:text-white inline-flex items-center gap-1.5"
                >
                  <Sparkles className="w-3.5 h-3.5" strokeWidth={3} /> Sign in
                </Link>
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="hub-follow-thanks-dismiss"
                  className="text-[11px] font-bold text-ink/60 hover:text-coral underline underline-offset-2"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            <p className="text-sm text-ink/80">
              We&apos;ll email you when this creator updates their page.
            </p>
            <div className="border-2 border-ink bg-sun p-3 shadow-brutalSm">
              <p className="font-display font-black text-base mb-1">Get live updates instead</p>
              <p className="text-[13px] text-ink/80 leading-relaxed">
                Create your <b>free Linkola account</b> in 30 seconds and:
              </p>
              <ul className="mt-2 space-y-1.5 text-[12px]">
                <li className="flex gap-2"><span className="text-coral font-black">→</span>
                  <span>Instant in-app notifications — no email delay, no spam folder.</span></li>
                <li className="flex gap-2"><span className="text-coral font-black">→</span>
                  <span>Your own <b>Web Link page</b> like this one, free forever.</span></li>
                <li className="flex gap-2"><span className="text-coral font-black">→</span>
                  <span>1-tap follow any creator, then all their updates land in your Feed.</span></li>
              </ul>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Link
                  to="/#signin"
                  onClick={onClose}
                  data-testid="hub-follow-thanks-signup"
                  className="border-2 border-ink bg-coral text-white px-3 py-2 font-bold text-xs shadow-brutalSm hover:bg-ink hover:text-white inline-flex items-center gap-1.5"
                >
                  <Sparkles className="w-3.5 h-3.5" strokeWidth={3} /> Create free account
                </Link>
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="hub-follow-thanks-dismiss"
                  className="text-[11px] font-bold text-ink/60 hover:text-coral underline underline-offset-2"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
    )
  );
}
